import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { mensagemDoErro } from '../../api/erros'
import { useConversa, usePerguntar, useSituacaoDaConsulta } from '../../api/queries/documentos'
import { BotaoLink } from '../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { TEXTOS } from '../../lib/glossario'
import { BoasVindas } from './BoasVindas'
import { CaixaDePergunta } from './CaixaDePergunta'
import { FioDaConversa } from './FioDaConversa'
import { ListaDeConversas } from './ListaDeConversas'
import { SemConsulta } from './SemConsulta'

/**
 * "Pergunte ao contrato": a conversa com o TR e os documentos do contrato.
 *
 * O endereço é a conversa (`/consulta/12`): voltar, recarregar e mandar o link para si mesmo abrem a
 * mesma conversa. A primeira pergunta de uma conversa nova a cria, e a tela passa para o endereço
 * dela sem perder o que está na tela.
 *
 * A caixa de pergunta fica presa ao pé da área de leitura, e o que está acima dela rola: a pergunta
 * seguinte está sempre à mão, como em qualquer conversa.
 *
 * @returns a página
 */
export function PaginaConsulta() {
  const t = TEXTOS.consulta
  const { id } = useParams()
  const conversaId = id ? Number(id) : null
  const navegar = useNavigate()
  const situacao = useSituacaoDaConsulta()
  const conversa = useConversa(conversaId)
  const perguntar = usePerguntar()
  const [texto, setTexto] = useState('')

  const disponivel = situacao.data?.disponivel ?? false
  const mensagens = conversa.data?.mensagens ?? []
  const pendente = perguntar.isPending ? (perguntar.variables?.pergunta ?? null) : null

  function enviar(pergunta: string) {
    const limpa = pergunta.trim()
    if (!limpa || perguntar.isPending) {
      return
    }
    perguntar.mutate(
      { conversaId, pergunta: limpa },
      {
        onSuccess: (resultado) => {
          setTexto('')
          if (conversaId !== resultado.conversaId) {
            navegar(`/consulta/${resultado.conversaId}`, { replace: conversaId === null })
          }
        },
      },
    )
  }

  const semDocumentos = situacao.isSuccess && situacao.data.documentosComTexto === 0
  const vazia = conversaId === null && !pendente

  return (
    <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
      <aside className="order-2 lg:order-1">
        <ListaDeConversas />
      </aside>

      <section className="order-1 flex min-h-[calc(100vh-11rem)] flex-col lg:order-2">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="titulo-da-pagina">{t.titulo}</h1>
            <p className="mt-1 max-w-prose text-sm text-slate-600">{t.descricao}</p>
          </div>
          <div className="flex items-center gap-2">
            {situacao.isSuccess && situacao.data.documentosComTexto > 0 && (
              <Link
                to="/documentos"
                className="rounded-full bg-white px-3 py-1 text-xs font-medium text-niteroi-700 ring-1 ring-slate-200 hover:bg-niteroi-50"
              >
                {t.lendo(situacao.data.documentosComTexto)}
              </Link>
            )}
            {/* No celular a lista de conversas fica embaixo do fio; começar outra precisa estar à mão. */}
            {conversaId !== null && (
              <BotaoLink to="/consulta" tamanho="pequeno" aparencia="secundario" className="lg:hidden">
                {t.novaConversa}
              </BotaoLink>
            )}
          </div>
        </header>

        <div className="flex-1 py-6">
          {situacao.isLoading && <Esqueleto linhas={3} />}
          {situacao.isError && <AvisoDeErro erro={situacao.error} />}
          {semDocumentos && <SemConsulta tipo="sem-documentos" />}
          {situacao.isSuccess && !semDocumentos && !disponivel && (
            <SemConsulta tipo="indisponivel" motivo={situacao.data.motivo} />
          )}
          {conversa.isError && <AvisoDeErro erro={conversa.error} />}
          {conversa.isLoading && <Esqueleto linhas={4} />}
          {vazia && !semDocumentos && <BoasVindas aoEscolher={enviar} desabilitada={!disponivel} />}
          {(mensagens.length > 0 || pendente) && (
            <FioDaConversa mensagens={mensagens} pendente={pendente} aoRepetir={enviar} />
          )}
        </div>

        {!semDocumentos && (
          <div className="sticky bottom-4 space-y-2">
            {perguntar.isError && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-inset ring-red-200">
                {mensagemDoErro(perguntar.error)}
              </p>
            )}
            <CaixaDePergunta
              texto={texto}
              aoMudar={setTexto}
              aoEnviar={() => enviar(texto)}
              desabilitada={!disponivel}
              ocupada={perguntar.isPending}
            />
          </div>
        )}
      </section>
    </div>
  )
}
